import { z } from 'zod';
import { isoDateTimeSchema, rawPhoneSchema, uuidSchema } from './common.js';
import { MAX_AMOUNT_CENTS } from './program-rules.js';
/** Ledger vocabulary — PROJECT-OVERVIEW.md §7. Do not introduce synonyms. */
export const EVENT_TYPES = ['EARN', 'REDEEM', 'ADJUST', 'EXPIRE', 'VOID'];
export const EVENT_SOURCES = [
    'STAFF_SCAN',
    'STAFF_MANUAL',
    'OFFLINE_SYNC',
    'API',
    'IMPORT',
    'SYSTEM',
];
/**
 * Resolve a customer at the counter. Exactly one of `qrToken` or `phoneE164` —
 * the phone path is the fallback for when a customer's battery is dead, and it
 * has to be as fast as scanning (ARCHITECTURE.md §6).
 */
export const loyaltyLookupSchema = z
    .object({
    brandId: uuidSchema,
    qrToken: z.string().min(1).optional().nullable(),
    phoneE164: rawPhoneSchema.optional().nullable(),
})
    .refine((v) => Boolean(v.qrToken) !== Boolean(v.phoneE164), {
    message: 'Provide exactly one of qrToken or phoneE164',
});
/**
 * Adding a customer to a Brand from the counter without its Program —
 * `POST /loyalty/check-in` (features/CHECK-INS.md). The same two ways in as a
 * lookup; the Branch is required because a check-in records where they joined.
 */
export const loyaltyCheckInSchema = z
    .object({
    brandId: uuidSchema,
    locationId: uuidSchema,
    qrToken: z.string().min(1).optional().nullable(),
    phoneE164: rawPhoneSchema.optional().nullable(),
})
    .refine((v) => Boolean(v.qrToken) !== Boolean(v.phoneE164), {
    message: 'Provide exactly one of qrToken or phoneE164',
});
export const loyaltyEarnSchema = z.object({
    brandId: uuidSchema,
    locationId: uuidSchema,
    /**
     * The card being stamped. Null for a customer the Brand does not have yet —
     * `lookup` no longer enrols (features/CHECK-INS.md) — who is then named by
     * `qrToken` or `phoneE164`, and this stamp creates their card.
     */
    enrollmentId: uuidSchema.nullable().default(null),
    /** A new customer's number, when they were looked up by phone. */
    phoneE164: rawPhoneSchema.nullable().default(null),
    /** Stamp count. Points programs derive their delta from `amountCents` instead. */
    quantity: z.number().int().min(1).max(10).default(1),
    /**
     * Bounded, because `delta` is an `int4` and a spend-threshold delta *is* the
     * amount. Unbounded, a large value was a Postgres numeric-overflow inside the
     * stamping transaction — a 500 at the counter rather than a clean refusal.
     */
    amountCents: z.number().int().min(0).max(MAX_AMOUNT_CENTS).nullable().default(null),
    qrToken: z.string().min(1).nullable().default(null),
    /**
     * When it happened at the counter. Set client-side so an offline event keeps its
     * real time; the server stamps `receivedAt` separately and trusts neither alone.
     */
    occurredAt: isoDateTimeSchema.optional(),
}).refine((v) => Boolean(v.enrollmentId) || Boolean(v.qrToken) || Boolean(v.phoneE164), {
    message: 'Provide the enrollmentId, or the qrToken or phoneE164 of a new customer',
});
export const loyaltyRedeemSchema = z.object({
    brandId: uuidSchema,
    locationId: uuidSchema,
    enrollmentId: uuidSchema,
    rewardId: uuidSchema,
});
/**
 * Adding a customer by phone from the dashboard — `POST /brands/:id/enrollments`.
 *
 * The phone is raw: the server normalises it to E.164 at the boundary
 * (AGENTS.md §5), so a merchant can type it the way it is written on a form.
 */
export const createEnrollmentSchema = z.strictObject({
    phoneE164: rawPhoneSchema,
    displayName: z.string().trim().min(1).max(120).optional(),
});
/** Manual correction. A reason is mandatory — this write lands in the audit log. */
export const loyaltyAdjustSchema = z.object({
    brandId: uuidSchema,
    enrollmentId: uuidSchema,
    delta: z.number().int().refine((v) => v !== 0, 'Adjustment must be non-zero'),
    reason: z.string().trim().min(3).max(500),
});
/** Reversal. Never an update or a delete — a VOID references the original event. */
export const loyaltyVoidSchema = z.object({
    brandId: uuidSchema,
    eventId: uuidSchema,
    reason: z.string().trim().min(3).max(500),
});
/**
 * How long a tier change stays news on the card — features/PROGRAMS.md. Long
 * enough that a customer who visits every week or two sees it once, short
 * enough that "You're now Gold" does not sit there for ever.
 */
export const TIER_NEWS_DAYS = 14;
//# sourceMappingURL=loyalty.js.map